import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  MyPenalty,
  Nullable,
  Penalty,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

const PENALTY_FIELDS = gql`
  fragment PenaltyFields on Penalty {
    id
    tournamentId
    playerId
    playerName
    judgeId
    matchId
    round
    tableNumber
    type
    category
    description
    status
    judgeCallId
    createdAt
    player {
      id
      name
      lastName
    }
    judge {
      id
      name
      lastName
    }
    match {
      id
      round
      tableNumber
    }
  }
`;

export type ApplyPenaltyInput = {
  tournamentId: number;
  // Uno de los dos: el jugador del sistema o su nombre escrito a mano.
  playerId?: number;
  playerName?: string;
  matchId?: number;
  // Sin matchId, ronda y mesa a mano: el API asocia la mesa si existe.
  round?: number;
  tableNumber?: number;
  // La pendiente (de una llamada) que se completa: el API la reemplaza.
  pendingPenaltyId?: number;
  type: string;
  category: string;
  description?: string;
};

export const applyPenalty = async (
  user: UserWithToken,
  input: ApplyPenaltyInput
) =>
  getGraphQLQueryClient(user).request<WithData<Penalty>>(
    gql`
      ${PENALTY_FIELDS}
      mutation applyPenalty($input: ApplyPenaltyInput!) {
        data: applyPenalty(input: $input) {
          ...PenaltyFields
        }
      }
    `,
    { input }
  );

export type UpdatePenaltyInput = {
  type?: string;
  category?: string;
  description?: string;
};

export const updatePenalty = async (
  user: UserWithToken,
  penaltyId: number,
  input: UpdatePenaltyInput
) =>
  getGraphQLQueryClient(user).request<WithData<Penalty>>(
    gql`
      ${PENALTY_FIELDS}
      mutation updatePenalty($penaltyId: Int!, $input: UpdatePenaltyInput!) {
        data: updatePenalty(penaltyId: $penaltyId, input: $input) {
          ...PenaltyFields
        }
      }
    `,
    { penaltyId, input }
  );

export const revokePenalty = async (
  user: UserWithToken,
  penaltyId: number
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation revokePenalty($penaltyId: Int!) {
        data: revokePenalty(penaltyId: $penaltyId)
      }
    `,
    { penaltyId }
  );

export const getTournamentPenalties = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Penalty[]>>(
    gql`
      ${PENALTY_FIELDS}
      query tournamentPenalties($tournamentId: Int!) {
        data: tournamentPenalties(tournamentId: $tournamentId) {
          ...PenaltyFields
        }
      }
    `,
    { tournamentId }
  );

/** Las anotadas al resolver una llamada, que falta completar. */
export const getPendingPenalties = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Penalty[]>>(
    gql`
      ${PENALTY_FIELDS}
      query pendingPenalties($tournamentId: Int!) {
        data: pendingPenalties(tournamentId: $tournamentId) {
          ...PenaltyFields
        }
      }
    `,
    { tournamentId }
  );

export const getPlayerPenalties = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  playerId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Penalty[]>>(
    gql`
      ${PENALTY_FIELDS}
      query playerPenalties($tournamentId: Int!, $playerId: Int!) {
        data: playerPenalties(tournamentId: $tournamentId, playerId: $playerId) {
          ...PenaltyFields
        }
      }
    `,
    { tournamentId, playerId }
  );

export const getMyTournamentPenalties = async (
  user: Nullable<UserWithToken>,
  tournamentId: number
) =>
  getGraphQLQueryClient(user).request<WithData<MyPenalty[]>>(
    gql`
      query myTournamentPenalties($tournamentId: Int!) {
        data: myTournamentPenalties(tournamentId: $tournamentId) {
          id
          tournamentId
          round
          type
          category
          description
          judgeName
          createdAt
        }
      }
    `,
    { tournamentId }
  );

export const getPlayerPenaltyHistory = async (
  user: UserWithToken,
  playerId: number,
  limit?: number
) =>
  getGraphQLQueryClient(user).request<WithData<Penalty[]>>(
    gql`
      ${PENALTY_FIELDS}
      query playerPenaltyHistory($playerId: Int!, $limit: Int) {
        data: playerPenaltyHistory(playerId: $playerId, limit: $limit) {
          ...PenaltyFields
        }
      }
    `,
    { playerId, limit: limit ?? null }
  );
